import type { Status } from "react-instantsearch-dom";

export enum VoiceSearchStatus {
    INITIAL = "initial",
    ASKING_PERMISSION = "askingPermission",
    WAITING = "waiting",
    RECOGNIZING = "recognizing",
    FINISHED = "finished",
    ERROR = "error",
}

export type VoiceSearchState = {
    status: VoiceSearchStatus | Status;
    transcript: string;
    errorCode: SpeechRecognitionErrorCode | null;
};

type CreateVoiceSearchParams = {
    language?: string;
    onStateChange(state: VoiceSearchState): void;
    onTranscript(transcript: string): void;
};

type VoiceSearchApi = {
    isBrowserSupported(): boolean;
    start(): void;
    stop(): void;
};

type DisclaimerStatus = {
    accepted: boolean;
    lastUpdated: Date;
};

function createState(state: Partial<VoiceSearchState>): VoiceSearchState {
    return {
        status: VoiceSearchStatus.INITIAL,
        transcript: "",
        errorCode: null,
        ...state,
    };
}
declare global {
    interface Window {
        webkitSpeechRecognition: SpeechRecognition;
        SpeechRecognition: SpeechRecognition;
    }
}

export const createVoiceSearch = ({
    language,
    onTranscript,
    onStateChange,
}: CreateVoiceSearchParams): VoiceSearchApi => {
    const SpeechRecognitionAPI: new () => SpeechRecognition =
        (window as Window).webkitSpeechRecognition || (window as Window).SpeechRecognition;
    let state: VoiceSearchState = createState({});
    let recognition: SpeechRecognition | undefined;

    const isBrowserSupported = () => {
        return Boolean(SpeechRecognitionAPI);
    };

    const setState = (newState: Partial<VoiceSearchState>) => {
        state = { ...state, ...newState };
        onStateChange(state);
    };

    const onStart = () => {
        setState({ status: VoiceSearchStatus.WAITING });
    };

    const onError = (event: SpeechRecognitionErrorEvent) => {
        setState({ status: VoiceSearchStatus.ERROR, errorCode: event.error });
    };

    const onResult = (event: SpeechRecognitionEvent) => {
        setState({
            status: VoiceSearchStatus.RECOGNIZING,
            transcript: (event?.results?.[0] && event?.results?.[0]?.[0] && event?.results?.[0]?.[0]?.transcript) || "",
        });
    };

    const onEnd = () => {
        if (!state.errorCode && state.transcript) {
            onTranscript(state.transcript);
        }

        if (state.status !== VoiceSearchStatus.ERROR) {
            setState(createState({ status: VoiceSearchStatus.INITIAL }));
        }
    };

    const start = () => {
        recognition = new SpeechRecognitionAPI();
        if (!recognition) {
            return;
        }

        setState(createState({ status: VoiceSearchStatus.ASKING_PERMISSION }));
        recognition.interimResults = true;
        if (language) {
            recognition.lang = language;
        }
        recognition.addEventListener("start", onStart);
        recognition.addEventListener("error", onError);
        recognition.addEventListener("result", onResult);
        recognition.addEventListener("end", onEnd);
        recognition.start();
    };

    const stop = () => {
        if (!recognition) {
            return;
        }

        recognition.stop();
        recognition.removeEventListener("start", onStart);
        recognition.removeEventListener("error", onError);
        recognition.removeEventListener("result", onResult);
        recognition.removeEventListener("end", onEnd);
        recognition = undefined;

        setState(createState({ status: VoiceSearchStatus.INITIAL }));
    };

    return {
        isBrowserSupported,
        start,
        stop,
    };
};

export const acceptVoiceSearchDisclaimer = (value: boolean): void => {
    const status = { accepted: value, lastUpdated: new Date() } as DisclaimerStatus;
    sessionStorage.setItem("voiceSearchDisclaimer", JSON.stringify(status));
};

export const isVoiceSearchDisclaimerAccepted = async (): Promise<boolean> => {
    let disclaimerAccepted = false;
    const status = sessionStorage.getItem("voiceSearchDisclaimer");

    // Check if the user has accepted the disclaimer
    if (status) {
        const { accepted, lastUpdated } = JSON.parse(status) as DisclaimerStatus;
        const lastUpdatedDate = new Date(lastUpdated);
        const diff = new Date().getTime() - lastUpdatedDate.getTime();
        const diffInMinutes = diff / (1000 * 60);

        // Check if the disclaimer was accepted within the last 120 minutes
        if (diffInMinutes < 120 && accepted) {
            disclaimerAccepted = true;
        }
    }

    return disclaimerAccepted;
};
